import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { Observable, of, Subject } from 'rxjs';
import { finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { moveInList } from '../../safety-rounds/safety-round.logic';
import { FactorSectionDraft, flattenFactorSections, toFactorSections } from '../exposure-register.logic';
import {
  ExposureRegulatoryReference,
  ExposureTemplateAssignment,
  ExposureTemplateDetail,
  ExposureTemplateFactor,
  ExposureTemplateStatus,
  SaveExposureTemplateRequest,
} from '../exposure-register.models';
import { FlexiExposureTemplateService } from './exposure-template.service';

/** The one factor open in its inline edit form. */
interface FactorEdit {
  section: number;
  index: number;
  /** A factor added just now; Cancel removes it again. */
  isNew: boolean;
  title: string;
  /** Paragraphs separated by a blank line. */
  guidance: string;
  references: ExposureRegulatoryReference[];
}

/**
 * Admin mode › Flexi › Settings › Exposure assessment templates › one template.
 *
 * Metadata, then one card per section with its exposure factors. One section
 * is open at a time and one factor is edited at a time. Save replaces the
 * whole template; Publish saves and then raises the version. Started
 * assessments keep the factors they were started with, so nothing here
 * changes an assessment in progress.
 */
@Component({
  selector: 'flexi-exposure-template-editor',
  templateUrl: './exposure-template-editor.component.html',
  // Same layout as the safety-round editor, so its stylesheet is reused as is.
  styleUrls: ['../../safety-rounds/templates/safety-round-template-editor.component.scss'],
  standalone: false,
})
export class ExposureTemplateEditorComponent implements OnInit, OnDestroy {
  readonly TemplateStatus = ExposureTemplateStatus;

  templateId = 0;
  template: ExposureTemplateDetail | null = null;
  loading = true;
  loadFailed = false;

  name = '';
  description = '';
  sections: FactorSectionDraft[] = [];

  /** Index of the section whose factors are shown. */
  openSection: number | null = 0;
  renamingSection: number | null = null;
  renameValue = '';
  editing: FactorEdit | null = null;
  submitted = false;

  saving = false;
  publishing = false;
  archiving = false;
  togglingDefault = false;

  assignments: ExposureTemplateAssignment[] = [];
  assignmentsLoading = false;
  assignOpen = false;
  removingIds = new Set<number>();

  /** The request as last loaded or saved — the dirty check compares against it. */
  private savedSnapshot = '';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: FlexiExposureTemplateService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private confirmation: ConfirmationService,
    private notifications: NotificationService,
  ) {}

  get isArchived(): boolean {
    return this.template?.Status === ExposureTemplateStatus.Archived;
  }

  get isPublished(): boolean {
    return this.template?.Status === ExposureTemplateStatus.Published;
  }

  get busy(): boolean {
    return this.saving || this.publishing || this.archiving;
  }

  get isDirty(): boolean {
    return !!this.template && (this.editing !== null || JSON.stringify(this.buildRequest()) !== this.savedSnapshot);
  }

  get factorCount(): number {
    return this.sections.reduce((sum, section) => sum + section.factors.length, 0);
  }

  get assignedDefaultCount(): number {
    return this.assignments.filter(a => a.IsDefault).length;
  }

  /** Why Save/Publish is blocked, or null. */
  get invalidKey(): string | null {
    if (!this.name.trim()) {
      return 'FLEXI_EXPOSURE.EDITOR.NAME_REQUIRED';
    }
    if (this.sections.some(section => section.factors.length > 0 && !section.name.trim())) {
      return 'FLEXI_EXPOSURE.EDITOR.SECTION_NAME_REQUIRED';
    }
    if (this.editing && !this.editing.title.trim()) {
      return 'FLEXI_EXPOSURE.EDITOR.FACTOR_TITLE_REQUIRED';
    }
    return null;
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.templateId = Number(params.get('templateId'));
      this.load();
      this.loadAssignments();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading = true;
    this.loadFailed = false;
    this.api
      .get(this.templateId)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.loading = false)))
      .subscribe({
        next: template => this.applyTemplate(template),
        error: () => {
          this.template = null;
          this.loadFailed = true;
        },
      });
  }

  private applyTemplate(template: ExposureTemplateDetail): void {
    this.template = template;
    this.name = template.Name;
    this.description = template.Description ?? '';
    this.sections = toFactorSections(
      (template.Factors ?? []).map(factor => ({ ...factor, RegulatoryReferences: factor.RegulatoryReferences ?? [] })),
    );
    this.editing = null;
    this.renamingSection = null;
    this.submitted = false;
    if (this.openSection === null || this.openSection >= this.sections.length) {
      this.openSection = this.sections.length ? 0 : null;
    }
    this.savedSnapshot = JSON.stringify(this.buildRequest());
  }

  loadAssignments(): void {
    this.assignmentsLoading = true;
    this.api
      .businesses(this.templateId)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.assignmentsLoading = false)))
      .subscribe({
        next: rows => (this.assignments = rows),
        error: () => (this.assignments = []),
      });
  }

  backToLibrary(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  private buildRequest(): SaveExposureTemplateRequest {
    return {
      Name: this.name.trim(),
      Description: this.description.trim() || null,
      Factors: flattenFactorSections(this.sections),
    };
  }

  /* ======================================================================
     Save, publish, archive
     ====================================================================== */

  /** Applies an open factor edit and saves. Emits false without a request when something is invalid. */
  private persist(): Observable<boolean> {
    this.submitted = true;
    if (this.invalidKey) {
      return of(false);
    }
    if (this.editing) {
      this.applyEdit();
    }
    const request = this.buildRequest();
    return this.api.save(this.templateId, request).pipe(
      map(() => {
        this.savedSnapshot = JSON.stringify(request);
        return true;
      }),
    );
  }

  save(): void {
    if (this.busy || this.isArchived) {
      return;
    }
    this.saving = true;
    this.persist()
      .pipe(takeUntil(this.destroy$), finalize(() => (this.saving = false)))
      .subscribe(saved => {
        if (saved) {
          this.notifications.success('UI_NOTIFICATIONS.SAVED');
          this.load();
        }
      });
  }

  /** Saves, then publishes as Version + 1. Started assessments keep their snapshot. */
  publish(): void {
    if (this.busy || this.isArchived) {
      return;
    }
    this.publishing = true;
    this.persist()
      .pipe(
        switchMap(saved => (saved ? this.api.publish(this.templateId).pipe(map(() => true)) : of(false))),
        takeUntil(this.destroy$),
        finalize(() => (this.publishing = false)),
      )
      .subscribe(published => {
        if (published) {
          this.notifications.success('FLEXI_EXPOSURE.EDITOR.PUBLISHED');
          this.load();
        }
      });
  }

  /** Separate from Save: the backend stores it outside the template body. */
  toggleDefault(): void {
    const template = this.template;
    if (!template || !this.isPublished || this.togglingDefault) {
      return;
    }
    const next = !template.IsDefaultForNewBusinesses;
    template.IsDefaultForNewBusinesses = next;
    this.togglingDefault = true;
    this.api
      .setDefaultForNew(template.Id, next)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.togglingDefault = false)))
      .subscribe({
        next: () => this.notifications.success('UI_NOTIFICATIONS.SAVED'),
        error: () => (template.IsDefaultForNewBusinesses = !next),
      });
  }

  confirmArchive(): void {
    if (this.busy || this.isArchived) {
      return;
    }
    this.confirmation.confirm({
      key: 'ferEditor',
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('FLEXI_EXPOSURE.EDITOR.CONFIRM_ARCHIVE', { name: this.name }),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        this.archiving = true;
        this.api
          .archive(this.templateId)
          .pipe(takeUntil(this.destroy$), finalize(() => (this.archiving = false)))
          .subscribe(() => {
            this.notifications.success('UI_NOTIFICATIONS.SAVED');
            this.load();
          });
      },
    });
  }

  /* ======================================================================
     Sections
     ====================================================================== */

  toggleSection(index: number): void {
    if (this.editing && this.editing.section !== index) {
      return;
    }
    this.openSection = this.openSection === index ? null : index;
  }

  addSection(): void {
    if (this.isArchived) {
      return;
    }
    this.sections = [...this.sections, { name: this.translate.instant('FLEXI_EXPOSURE.EDITOR.NEW_SECTION'), factors: [] }];
    this.openSection = this.sections.length - 1;
    this.startRename(this.sections.length - 1);
  }

  startRename(index: number): void {
    this.renamingSection = index;
    this.renameValue = this.sections[index]?.name ?? '';
  }

  commitRename(): void {
    const index = this.renamingSection;
    if (index === null) {
      return;
    }
    const name = this.renameValue.trim();
    if (name) {
      this.sections[index].name = name;
    }
    this.renamingSection = null;
  }

  cancelRename(): void {
    this.renamingSection = null;
  }

  moveSection(index: number, delta: number): void {
    if (this.editing) {
      return;
    }
    const target = index + delta;
    this.sections = moveInList(this.sections, index, delta);
    if (this.openSection === index && target >= 0 && target < this.sections.length) {
      this.openSection = target;
    } else if (this.openSection === target) {
      this.openSection = index;
    }
  }

  removeSection(index: number): void {
    const section = this.sections[index];
    if (!section || this.editing) {
      return;
    }
    const remove = () => {
      this.sections = this.sections.filter((_, i) => i !== index);
      this.openSection = this.sections.length ? Math.min(index, this.sections.length - 1) : null;
    };
    if (!section.factors.length) {
      remove();
      return;
    }
    this.confirmation.confirm({
      key: 'ferEditor',
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('FLEXI_EXPOSURE.EDITOR.CONFIRM_REMOVE_SECTION', {
        name: section.name,
        count: section.factors.length,
      }),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: remove,
    });
  }

  /* ======================================================================
     Factors
     ====================================================================== */

  /** "2 legal references · guidance" */
  factorMeta(factor: ExposureTemplateFactor): string {
    const parts: string[] = [];
    if (factor.Guidance) {
      parts.push(this.translate.instant('FLEXI_EXPOSURE.EDITOR.META_GUIDANCE'));
    }
    if (factor.RegulatoryReferences.length) {
      parts.push(this.translate.instant('FLEXI_EXPOSURE.EDITOR.META_REFERENCES', { count: factor.RegulatoryReferences.length }));
    }
    return parts.join(' · ');
  }

  isEditing(section: number, index: number): boolean {
    return this.editing?.section === section && this.editing.index === index;
  }

  addFactor(sectionIndex: number): void {
    if (this.isArchived || this.editing) {
      return;
    }
    const section = this.sections[sectionIndex];
    section.factors = [...section.factors, {
      SectionName: section.name,
      SectionPosition: sectionIndex + 1,
      Position: section.factors.length + 1,
      Title: '',
      Guidance: null,
      RegulatoryReferences: [],
    }];
    this.openEdit(sectionIndex, section.factors.length - 1, true);
  }

  editFactor(section: number, index: number): void {
    if (this.isArchived || this.editing) {
      return;
    }
    this.openEdit(section, index, false);
  }

  private openEdit(sectionIndex: number, index: number, isNew: boolean): void {
    const factor = this.sections[sectionIndex].factors[index];
    this.editing = {
      section: sectionIndex,
      index,
      isNew,
      title: factor.Title,
      guidance: factor.Guidance ?? '',
      references: factor.RegulatoryReferences.map(ref => ({ ...ref })),
    };
    this.openSection = sectionIndex;
  }

  /** Writes the edit form back into the section. */
  applyEdit(): void {
    const edit = this.editing;
    if (!edit || !edit.title.trim()) {
      return;
    }
    const section = this.sections[edit.section];
    const factors = [...section.factors];
    factors[edit.index] = {
      ...factors[edit.index],
      Title: edit.title.trim(),
      Guidance: edit.guidance.trim() || null,
      RegulatoryReferences: edit.references
        .map(ref => ({ Title: ref.Title.trim(), Url: ref.Url?.trim() || null }))
        .filter(ref => ref.Title.length > 0),
    };
    section.factors = factors;
    this.editing = null;
  }

  cancelEdit(): void {
    const edit = this.editing;
    if (!edit) {
      return;
    }
    if (edit.isNew) {
      const section = this.sections[edit.section];
      section.factors = section.factors.filter((_, i) => i !== edit.index);
    }
    this.editing = null;
  }

  removeEditedFactor(): void {
    const edit = this.editing;
    if (!edit) {
      return;
    }
    const section = this.sections[edit.section];
    section.factors = section.factors.filter((_, i) => i !== edit.index);
    this.editing = null;
  }

  /** Moves the factor being edited, keeping its form open. */
  moveEditedFactor(delta: number): void {
    const edit = this.editing;
    if (!edit) {
      return;
    }
    const section = this.sections[edit.section];
    const target = edit.index + delta;
    if (target < 0 || target >= section.factors.length) {
      return;
    }
    section.factors = moveInList(section.factors, edit.index, delta);
    edit.index = target;
  }

  addReference(): void {
    this.editing?.references.push({ Title: '', Url: null });
  }

  removeReference(index: number): void {
    this.editing?.references.splice(index, 1);
  }

  /* ======================================================================
     Assigned businesses
     ====================================================================== */

  openAssign(): void {
    if (this.isPublished) {
      this.assignOpen = true;
    }
  }

  assignClosed(saved: boolean): void {
    this.assignOpen = false;
    if (saved) {
      this.loadAssignments();
    }
  }

  confirmUnassign(assignment: ExposureTemplateAssignment): void {
    this.confirmation.confirm({
      key: 'ferEditor',
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('FLEXI_EXPOSURE.EDITOR.CONFIRM_UNASSIGN', { name: assignment.BusinessName }),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        this.removingIds.add(assignment.BusinessId);
        this.api
          .unassign(this.templateId, assignment.BusinessId)
          .pipe(takeUntil(this.destroy$), finalize(() => this.removingIds.delete(assignment.BusinessId)))
          .subscribe(() => {
            this.notifications.success('UI_NOTIFICATIONS.DELETED');
            this.loadAssignments();
          });
      },
    });
  }
}
